import type { FC } from 'react';

import { useContent, useSetting } from '@eneco/cms';
import RichText from '@eneco/components/RichText/RichText';
import { PromoBox as PromoBoxComponent } from '@eneco/custom-components/PromoBox';

import type { PromoBoxContent } from './promo-box-content.types';

const DESKTOP_POSITIONS = ['top-left', 'top-right', 'bottom-left', 'bottom-right'] as const;
const MOBILE_POSITIONS = ['top-left', 'top-right'] as const;

const oneOf = <T extends string>(options: readonly T[], value: string | undefined): T | undefined =>
  options.find(option => option === value);

/**
 * `PromoBox` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy component (`libs/sitecore/components/content/src/PromoBox/PromoBox.tsx`): a circular
 * badge pinned to a corner of whatever it sits in (`jss-promo-box` of `Hero`/`SplitView`), with an optional
 * top subtitle, a rich-text title and an optional bottom subtitle. The badge itself — markup and
 * stylesheet — is `@eneco/custom-components/PromoBox`, moved out verbatim because its positioning and type
 * scale are custom CSS this lane may not carry (same arrangement as `ChatEntryPoint`).
 *
 * `setElevationFlag` is `'1'` on Sitecore and a boolean (`'true'` through `useSetting`) on Sanity; both
 * mean elevated. An unrecognised position falls back to the badge's defaults (top-right).
 */
const PromoBox: FC = () => {
  const content = useContent<PromoBoxContent>();
  const { title, topSubTitle, bottomSubTitle } = content.content ?? content;

  const elevation = useSetting('setElevationFlag');

  return (
    <PromoBoxComponent
      title={title ? <RichText html={title} /> : undefined}
      topSubTitle={topSubTitle}
      bottomSubTitle={bottomSubTitle}
      backgroundColorOption={useSetting('backgroundColorOption')}
      isElevated={elevation === '1' || elevation === 'true'}
      desktopPosition={oneOf(DESKTOP_POSITIONS, useSetting('desktopVariantOption'))}
      mobilePosition={oneOf(MOBILE_POSITIONS, useSetting('mobileVariantOption'))}
    />
  );
};

export default PromoBox;
